/**
 * usePracticeTextStyles.ts — Extracted from PracticeView.vue Phase 5a
 *
 * Contains AnnotatedText style callback functions used by the practice overlay:
 * sentenceStyle, segmentBoundaryStyle, segmentStyle, clickableSentence, clickableSegment.
 * This is a purely mechanical extraction with zero logic changes.
 *
 * State remains in PracticeView's reactive object; this composable operates
 * on Ref-wrapped state properties passed via the deps interface.
 */
import type { SentenceType } from 'types/types'
import { ComputedRef, Ref } from 'vue'
import { VueLogger } from 'vue-logger-plugin'

import { AnnotatedSegment } from '@/util/AnnotatedSegment'
import { AnnotatedSentence } from '@/util/AnnotatedSentence'
import type { PitchShiftType, SentenceRecordingMetadataWithPitchedAudios } from '@/util/AudioUtil'
import { absentStyle, regularStyle, SEGMENT_STYLES } from '@/util/TextStyles'
import { inTextMode, VoiceStyleSet } from '@/util/VoiceStyles'

// {{{ interface
export interface PracticeTextStylesDeps {
  // State refs (from PracticeView's toRefs)
  downloading: Ref<boolean>
  focusSentenceIndex: Ref<number | null>
  pitchShift: Ref<PitchShiftType>
  voiceStyleSet: Ref<VoiceStyleSet>

  // Computed refs
  isRecording: ComputedRef<boolean>
  recordingsInStyle: ComputedRef<Record<string, SentenceRecordingMetadataWithPitchedAudios[]>>
  surroundBeforeStartIndex: ComputedRef<number>
  surroundBeforeSentences: ComputedRef<Array<SentenceType>>

  // From useFollower
  currentSegmentIndex: Ref<number>
  currentSentenceIndex: Ref<number>
  followerSegmentStyle: (style: string, annotatedSegment: AnnotatedSegment, sentenceIndex: number, segmentIndex: number) => string

  // From useAudioPlayer
  isPlayable: (params: {
    pitchShift: PitchShiftType
    segmentIndex: number
    sentenceRecordingMetadataWithPitchedAudios: SentenceRecordingMetadataWithPitchedAudios
  }) => boolean

  // From usePracticeNavigation
  hasRecordingByAnnotatedSentence: (annotatedSentence: AnnotatedSentence) => boolean
  hasRecordingBySentenceIndex: (idx: number) => boolean
}
// }}}

export function usePracticeTextStyles(logger: VueLogger, deps: PracticeTextStylesDeps) {
  // {{{ sentenceStyle
  /**
   * @postcondition Returns regularStyle if in text mode or sentence has recording; absentStyle otherwise
   * @depends-on deps.voiceStyleSet, deps.recordingsInStyle
   */
  function sentenceStyle(__: string, annotatedSentence: AnnotatedSentence): string {
    return inTextMode(deps.voiceStyleSet.value) || deps.hasRecordingByAnnotatedSentence(annotatedSentence) ? regularStyle : absentStyle
  }
  // }}}
  // {{{ segmentBoundaryStyle
  /**
   * @postcondition During recording: returns style unchanged. Otherwise: alternating SEGMENT_STYLES
   *   for the current sentence, regular style for other sentences.
   * @depends-on deps.isRecording, deps.currentSentenceIndex
   */
  function segmentBoundaryStyle(
    style: string,
    __: AnnotatedSentence,
    ___: AnnotatedSegment,
    sentenceIndex: number,
    segmentIndex: number,
  ): string {
    if (deps.isRecording.value) {
      return style
    }
    return sentenceIndex === deps.currentSentenceIndex.value ? SEGMENT_STYLES[segmentIndex % 2] : style
  }
  // }}}
  // {{{ segmentStyle
  /**
   * @postcondition During recording: delegates to followerSegmentStyle for VAD-driven highlighting.
   *   Not recording: alternating SEGMENT_STYLES, with clickable segments getting special treatment.
   *   Non-focus sentences always get regularStyle.
   * @depends-on deps.isRecording, deps.focusSentenceIndex, deps.surroundBeforeSentences, deps.currentSegmentIndex,
   *   deps.followerSegmentStyle, clickableSegment
   */
  function segmentStyle(
    style: string,
    annotatedSentence: AnnotatedSentence,
    annotatedSegment: AnnotatedSegment,
    sentenceIndex: number,
    segmentIndex: number,
  ): string {
    // we're being asked about the style of a sentence that isn't the focus sentence; it's regular
    const nPreSentences = deps.surroundBeforeSentences.value.length
    if (deps.focusSentenceIndex.value !== null && sentenceIndex !== nPreSentences) {
      return regularStyle
    }

    // if we're recording, styling segments is the responsibility of the follower
    if (deps.isRecording.value) {
      return deps.followerSegmentStyle(style, annotatedSegment, sentenceIndex, segmentIndex)
    }

    // alternate segment coloring
    if (segmentIndex !== deps.currentSegmentIndex.value) {
      return SEGMENT_STYLES[segmentIndex % 2]
    }

    // The current segment (segmentIndex === currentSegmentIndex) is always shown as absent
    // because it's the active/playing segment — it can't be "clicked" to play itself.
    return absentStyle
  }
  // }}}

  // {{{ clickableSentence
  /**
   * @postcondition Returns true if not recording AND sentence has recording AND is not the focus sentence
   * @depends-on deps.isRecording, deps.surroundBeforeStartIndex, deps.focusSentenceIndex
   */
  function clickableSentence(__: AnnotatedSentence, sentenceIndex: number): boolean {
    if (deps.isRecording.value) {
      return false
    }

    const absoluteSentenceIndex = deps.surroundBeforeStartIndex.value + sentenceIndex
    return deps.hasRecordingBySentenceIndex(absoluteSentenceIndex) && absoluteSentenceIndex !== deps.focusSentenceIndex.value
  }
  // }}}
  // {{{ clickableSegment
  /**
   * @postcondition Returns true if not downloading, is the focus sentence, is not the current segment,
   *   has exactly one recording in style, and that segment is playable at current pitch
   * @depends-on deps.downloading, deps.surroundBeforeStartIndex, deps.focusSentenceIndex, deps.currentSegmentIndex,
   *   deps.recordingsInStyle, deps.isPlayable, deps.pitchShift
   */
  function clickableSegment(
    annotatedSentence: AnnotatedSentence,
    __: AnnotatedSegment,
    sentenceIndex: number,
    segmentIndex: number,
  ): boolean {
    // can't click on audio that's still downloading
    if (deps.downloading.value) {
      return false
    }

    // can't click on individual segments in a sentence that isn't the current focus
    const absoluteSentenceIndex = deps.surroundBeforeStartIndex.value + sentenceIndex
    if (absoluteSentenceIndex !== deps.focusSentenceIndex.value) {
      return false
    }

    // can't click on a segment that's already the current segment
    if (segmentIndex === deps.currentSegmentIndex.value) {
      return false
    }

    if (!deps.recordingsInStyle.value[annotatedSentence.sentence().id]) {
      logger.error('unexpected null recordingsInStyle.value[annotatedSentence.sentence().id]')
      return false
    }

    if (!deps.recordingsInStyle.value[annotatedSentence.sentence().id].length) {
      logger.error('unexpect zero length recordingsInStyle.value[annotatedSentence.sentence().id].length')
      return false
    }

    if (deps.recordingsInStyle.value[annotatedSentence.sentence().id].length > 1) {
      logger.error('unexpect >1 length recordingsInStyle.value[annotatedSentence.sentence().id]')
      return false
    }

    return deps.isPlayable({
      pitchShift: deps.pitchShift.value,
      segmentIndex,
      sentenceRecordingMetadataWithPitchedAudios: deps.recordingsInStyle.value[annotatedSentence.sentence().id][0],
    })
  }
  // }}}

  return {
    clickableSegment,
    clickableSentence,
    segmentBoundaryStyle,
    segmentStyle,
    sentenceStyle,
  }
}
